/**
 * 탑 플롯(m/z vs RT) 덧그림 층의 좌표 계산과 그리기.
 *
 * 2026-09-03. 호버 강조(검은 링)와 선택 표시를 recharts 안의 `<Scatter>` 로
 * 그리면, 표시가 바뀔 때마다 **차트 전체가 다시 만들어진다**. 실측:
 *
 *   · 점 485개 / SVG 요소 1,117개(페이지 전체 DOM 의 48%)
 *   · 스냅 대상이 바뀔 때마다 재생성 — 1회당 307ms (TIC 캔버스는 0.83ms)
 *   · 마우스로 좌→우 한 번 훑으면 80회 → dev 스윕 21.6초 중 18.7초 메인 스레드 블록
 *
 * 그래서 표시를 차트 **위에 얹은 덧그림 층**으로 옮긴다. 커서 점선·라벨이
 * 이미 쓰고 있는 방식과 같다(실측 1~3ms).
 *
 * ## 좌표를 어디서 얻는가
 *
 * 차트가 **실제로 쓰는 도메인**과 **실제로 그려진 플롯 영역**(recharts 의
 * 격자 사각형)에서 얻는다. 도메인 함수는 `<XAxis domain>` · `<YAxis domain>`
 * 에 넘기는 것과 같은 것을 쓰므로 둘이 어긋날 수 없고, 플롯 영역은 화면에
 * 그려진 것이라 역시 어긋날 수 없다. 같은 파일의 커서 점선이 x축에 대해
 * 이미 이 방식을 쓰고 있다(운영자 검토에서 위치가 맞는 것이 확인된 코드다).
 *
 * 축 눈금 라벨을 읽는 방법도 검토했으나 버렸다 — 이 recharts 판에서는 눈금
 * 라벨이 축 그룹 **밖**에 렌더되어(실측: `.recharts-yAxis` 안의 라벨 0개,
 * 눈금선 5개) 어느 축의 것인지 DOM 만으로는 확정할 수 없었다.
 */

/** 덧그림 층에 그릴 표식 — 좌표(기준 요소 왼쪽 위로부터 px)와 점의 색. */
export interface OverlayMark {
  x: number;
  y: number;
  /** 원래 점과 **같은 색**. 커진 점이 다른 색이면 다른 점처럼 보인다. */
  color: string;
}

/** 플롯 영역(기준 요소 기준 px). */
export interface PlotRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * m/z 축 도메인.
 *
 * **`MZRTChartRecharts` 의 `<YAxis domain>` 과 같은 규칙이어야 한다** — 한쪽만
 * 바꾸면 표식이 점에서 어긋난다. 그래서 여기 한 곳에 둔다.
 */
export function mzDomain(points: { mz: number }[]): [number, number] | null {
  if (points.length === 0) return null;
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    if (!Number.isFinite(point.mz)) continue;
    if (point.mz < min) min = point.mz;
    if (point.mz > max) max = point.mz;
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return [Math.max(min - 10, 0), max];
}

/**
 * 값(x축 값, m/z)을 덧그림 층 좌표로 옮긴다.
 *
 * 도메인 폭이 0 이면(점이 하나뿐인 경우 등) **빈 배열**을 돌려준다 — 위치를
 * 모르는 채 표식을 그리면 증거 화면에 엉뚱한 곳을 가리키는 표시가 남는다.
 * 그리지 않는 편이 낫다.
 */
export function projectScanMarks(
  points: { x: number; mz: number; color: string }[],
  plot: PlotRect,
  xDomain: [number, number],
  yDomain: [number, number],
): OverlayMark[] {
  if (points.length === 0) return [];

  const [xMin, xMax] = xDomain;
  const [yMin, yMax] = yDomain;
  const xSpan = xMax - xMin;
  const ySpan = yMax - yMin;
  if (xSpan === 0 || ySpan === 0) return [];
  if (!Number.isFinite(xSpan) || !Number.isFinite(ySpan)) return [];

  return points.map((point) => ({
    x: plot.left + ((point.x - xMin) / xSpan) * plot.width,
    // m/z 는 위로 갈수록 크다 — 화면 y 는 반대다.
    y: plot.top + plot.height - ((point.mz - yMin) / ySpan) * plot.height,
    color: point.color,
  }));
}

/**
 * 그려진 플롯 영역을 기준 요소 좌표로 읽는다.
 *
 * recharts 의 격자(`.recharts-cartesian-grid`)가 곧 플롯 영역이다.
 */
export function plotRectWithin(root: Element | null): PlotRect | null {
  if (!root) return null;
  const grid = root.querySelector(".recharts-cartesian-grid");
  if (!grid) return null;

  const rootRect = root.getBoundingClientRect();
  const gridRect = grid.getBoundingClientRect();
  if (gridRect.width === 0 || gridRect.height === 0) return null;

  return {
    left: gridRect.left - rootRect.left,
    top: gridRect.top - rootRect.top,
    width: gridRect.width,
    height: gridRect.height,
  };
}

/**
 * recharts 가 **실제로 그린** 점의 반지름(px).
 *
 * 덧그림 강조를 원래 표시와 같은 크기로 그리기 위해 필요하다. 값을 상수로
 * 박으면 안 된다 — recharts 의 `<Scatter r={...}>` 은 **크기에 반영되지 않는다.**
 * 실측: 같은 차트에서 `r={3}`(회색 점)과 `r={6}`(동정 점) 이 **둘 다 반지름
 * 4.51px** 로 그려졌다(기본 심볼 면적 64 → √(64/π)). 그래서 종전 강조 링에
 * 적혀 있던 `r={9}` 도 실제로는 4.51px 였고, 이걸 9px 로 옮겨 그렸더니 표시가
 * 두 배로 커져 어색해졌다(운영자 지적 2026-09-03).
 *
 * 그려진 것에서 읽으므로 심볼 크기 설정이 바뀌어도 따라간다.
 */
export function renderedPointRadius(root: Element | null): number | null {
  const symbol = root?.querySelector(".recharts-symbols");
  if (!symbol) return null;
  const rect = symbol.getBoundingClientRect();
  if (rect.width === 0) return null;
  return rect.width / 2;
}

/**
 * 강조 표식의 반지름 — **점이 커진 것처럼** 보이게.
 *
 * 2026-09-04 (운영자): "단순 동그라미 그리지 말고 **도트가 커지는 걸로** 시각적
 * 표현해 줘. 성능 이슈가 있으니 기존 도트 노드를 직접 건드리지 말고, 시각적으로
 * 같은(또는 동등한) 색에서 강조하는 형태로."
 *
 * 그래서 차트의 점은 그대로 두고, 그 위에 **같은 색으로 채운 더 큰 원**을
 * 덧그림 층에 얹는다. 원래 점은 그 밑에 완전히 가려지므로 "점이 커졌다"로
 * 보이고, recharts 는 다시 그려지지 않는다(리렌더 0).
 *
 * 테두리를 두르는 종전 방식(링)은 버렸다 — 운영자 검토에서 "붕 뜬다"·"확대
 * 표시가 어색하다"는 지적을 받았다.
 */
export function enlargedDotRadius(pointRadius: number): number {
  return pointRadius * 1.8;
}

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * 강조 점의 테두리 색 — 거의 검정(slate-900).
 *
 * 2026-09-06 (운영자): "테두리는 없거나 검정색으로 딱 붙여서 라인만 그을까?
 * … 점 커지고 라인을 더 키우는건 안좋아."
 *
 * 그래서 테두리를 **점 가장자리에 걸쳐** 긋는다(중심이 반지름 위). 굵기의
 * 절반만 바깥으로 나가므로 표식이 커 보이지 않는다. 색은 램프 전 구간과
 * OKLab 28.5(하한 15)·대비 최소 1.99:1 로 갈리고 흰 배경과는 17.9:1 이다 —
 * 종전 파랑 `#2563eb` 는 램프와 OKLab 11.7 이라 violet 위에서 묻혔다.
 */
const HIGHLIGHT_EDGE_COLOR = "#0f172a";
/** 호버 테두리 굵기(px) — 얇게. 절반(0.5px)만 바깥으로 나간다. */
const HOVER_EDGE_WIDTH = 1;

/**
 * 덧그림 층을 다시 그린다 — **React 를 거치지 않는다.**
 *
 * 이 층의 존재 이유가 "표시가 바뀔 때 차트를 다시 만들지 않는 것"이므로,
 * 여기서 상태를 갱신하면 아무것도 나아지지 않는다.
 *
 * 각 표식은 **원래 점과 같은 색으로 채운 더 큰 원**이다(점이 커진 것처럼).
 *
 * 표식은 **커진 점 하나**다 — 원래 점과 같은 색으로 채우고, 가장자리에
 * 걸쳐 거의 검정 테두리를 두른다. 테두리 중심이 반지름 위에 있으므로 굵기의
 * 절반만 바깥으로 나가고, 표식은 "커진 점"으로 읽힌다.
 *
 * 테두리가 필요한 이유(2026-09-06): 동정 점이 전부 같은 violet 램프가 되면서
 * 커진 점이 **이웃과 같은 색**이 됐다. 빽빽한 구간에서는 1.8배로 키워도
 * 눈에 띄지 않는다 — 종전에는 관찰마다 색이 달라 크기 차이가 읽혔다.
 *
 * 테두리를 채움 안쪽이나 바깥쪽 **링**으로 두는 안은 둘 다 버렸다. 안쪽에
 * 두면 채움을 깎아 속 빈 도넛이 되고("단순 동그라미 그리지 말고 도트가
 * 커지는 걸로", 2026-09-03), 바깥에 두면 표식이 실제보다 커 보인다
 * ("점 커지고 라인을 더 키우는건 안좋아", 2026-09-06).
 *
 * @param outline 선택 표시에 쓴다 — 테두리를 더 굵게 그어 호버와 가른다.
 *        `stroke` 값은 쓰지 않는다(색은 두 상태가 같다). 굵기만 다르다.
 */
export function paintScanHighlight(
  svg: SVGSVGElement | null,
  marks: OverlayMark[],
  options: {
    radius: number;
    outline?: { width: number };
  },
): void {
  if (!svg) return;

  svg.replaceChildren();
  if (marks.length === 0) return;

  for (const mark of marks) {
    const circle = document.createElementNS(SVG_NS, "circle");
    circle.setAttribute("cx", String(mark.x));
    circle.setAttribute("cy", String(mark.y));
    circle.setAttribute("r", String(options.radius));
    circle.setAttribute("fill", mark.color);
    // 테두리는 **점 가장자리에 걸쳐** 긋는다 — 반지름 위에 중심이 놓이므로
    // 굵기의 절반만 바깥으로 나간다. 표식이 커 보이지 않고, 채움도 깎이지
    // 않는다(안쪽 절반은 어차피 같은 점 위다).
    circle.setAttribute("stroke", HIGHLIGHT_EDGE_COLOR);
    circle.setAttribute(
      "stroke-width",
      String(options.outline?.width ?? HOVER_EDGE_WIDTH),
    );
    svg.appendChild(circle);
  }
}
